import { useBackgroundQuery } from "@apollo/client";
import {
  InvoiceAuditLinesQueryVariables,
  InvoiceAuditReportQueryVariables,
} from "generated-graphql/graphql";
import { useCallback, useDeferredValue, useMemo, useState } from "react";

import {
  INVOICE_AUDIT_DETAIL_SUMMARY,
  INVOICE_AUDIT_LINES,
  INVOICE_AUDIT_REPORT,
  INVOICE_AUDIT_REPORT_SUMMARY,
} from "./queries";

function useAuditQueryRetry(refetch: () => Promise<unknown>) {
  const [retry, setRetry] = useState(0);
  const onRetry = useCallback(() => {
    // Refetch the rejected query before resetting its error boundary.
    // The boundary displays any subsequent failure.
    void refetch().catch(() => undefined);
    setRetry((value) => value + 1);
  }, [refetch]);
  return { retry, onRetry };
}

export function useInvoiceAuditReportQueries(variables: InvoiceAuditReportQueryVariables) {
  const summaryVariables = useMemo(
    () => ({ tenantId: variables.tenantId, search: variables.search }),
    [variables.tenantId, variables.search]
  );
  const deferredVariables = useDeferredValue(variables);
  const deferredSummaryVariables = useDeferredValue(summaryVariables);
  const [queryRef, { refetch }] = useBackgroundQuery(INVOICE_AUDIT_REPORT, {
    variables: deferredVariables,
    fetchPolicy: "network-only",
  });
  const [summaryRef, { refetch: refetchSummary }] = useBackgroundQuery(
    INVOICE_AUDIT_REPORT_SUMMARY,
    {
      variables: deferredSummaryVariables,
      fetchPolicy: "network-only",
    }
  );
  const { retry, onRetry } = useAuditQueryRetry(refetch);
  const { retry: summaryRetry, onRetry: onSummaryRetry } = useAuditQueryRetry(refetchSummary);
  return {
    queryRef,
    retry,
    onRetry,
    summaryRef,
    summaryRetry,
    onSummaryRetry,
    isPending: deferredVariables !== variables,
    summaryPending: deferredSummaryVariables !== summaryVariables,
  };
}

export function useInvoiceAuditDetailQueries(variables: InvoiceAuditLinesQueryVariables) {
  const summaryVariables = useMemo(
    () => ({ id: variables.id, facilityId: variables.facilityId, search: variables.search }),
    [variables.id, variables.facilityId, variables.search]
  );
  const deferredVariables = useDeferredValue(variables);
  const deferredSummaryVariables = useDeferredValue(summaryVariables);
  const [queryRef, { refetch }] = useBackgroundQuery(INVOICE_AUDIT_LINES, {
    variables: deferredVariables,
    fetchPolicy: "network-only",
  });
  const [summaryRef, { refetch: refetchSummary }] = useBackgroundQuery(
    INVOICE_AUDIT_DETAIL_SUMMARY,
    {
      variables: deferredSummaryVariables,
      fetchPolicy: "network-only",
    }
  );
  const { retry, onRetry } = useAuditQueryRetry(refetch);
  const { retry: summaryRetry, onRetry: onSummaryRetry } = useAuditQueryRetry(refetchSummary);
  return {
    queryRef,
    retry,
    onRetry,
    summaryRef,
    summaryRetry,
    onSummaryRetry,
    isPending: deferredVariables !== variables,
    summaryPending: deferredSummaryVariables !== summaryVariables,
  };
}
